import { useEffect, useRef } from 'react';
import { useWhatsAppLead } from '@/hooks/useWhatsAppLead';

export default function StatementAndCTA() {
  const ref = useRef<HTMLDivElement>(null);
  const { openModal } = useWhatsAppLead();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll('.cta-reveal');
            items.forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* STATEMENT SECTION */}
      <section
        className="py-20 md:py-28 relative overflow-hidden"
        style={{
          background: 'radial-gradient(ellipse at 50% 100%, rgba(240,180,41,0.08) 0%, transparent 60%), linear-gradient(180deg, #0a0a0f 0%, #0a0a0f 100%)',
        }}
      >
        <div className="absolute top-0 left-0 right-0 section-divider" />
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: 'linear-gradient(rgba(240,180,41,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(240,180,41,0.8) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }} />

        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <p className="text-3xl md:text-4xl lg:text-5xl font-black text-white leading-tight tracking-tight">
            "Siteniz olsun diye değil,{' '}
            <br className="hidden md:block" />
            <span className="text-gold-gradient">müşteri gelsin diye</span>{' '}
            kuruyoruz."
          </p>
          <div className="w-24 h-1 bg-gold rounded-full mx-auto mt-8 opacity-60" />
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section
        className="py-24 md:py-32 relative overflow-hidden"
        style={{ background: '#0d0d15' }}
        ref={ref}
      >
        <div className="absolute top-0 left-0 right-0 section-divider" />
        <div
          className="absolute inset-0 opacity-10"
          style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(240,180,41,0.15), transparent 70%)' }}
        />

        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          {/* Urgency badge */}
          <div
            className="cta-reveal inline-flex items-center gap-2 bg-red-900/20 border border-red-500/30 px-4 py-2 rounded-full mb-8"
            style={{ opacity: 0, transform: 'translateY(20px)', transition: 'all 0.5s ease' }}
          >
            <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse-dot" />
            <span className="text-red-400 text-sm font-semibold">Sınırlı Kontenjan — Bu Ay Son 3 Yer</span>
          </div>

          <h2
            className="cta-reveal text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 tracking-tight"
            style={{ opacity: 0, transform: 'translateY(20px)', transition: 'all 0.5s ease' }}
          >
            Hemen <span className="text-gold-gradient">Başlayın</span>
          </h2>

          <p
            className="cta-reveal text-gray-400 text-lg mb-10 max-w-xl mx-auto leading-relaxed"
            style={{ opacity: 0, transform: 'translateY(20px)', transition: 'all 0.5s ease' }}
          >
            24 saat içinde kurulum başlar. İşletmenize özel, dönüşüm odaklı dijital satış platformunuz kısa sürede yayında.
          </p>

          <div
            className="cta-reveal flex flex-col sm:flex-row items-center justify-center gap-4 mb-10"
            style={{ opacity: 0, transform: 'translateY(20px)', transition: 'all 0.5s ease' }}
          >
            <button
              onClick={() => openModal('Final CTA — Demo Talep Et')}
              className="btn-gold px-10 py-4 rounded-xl text-base font-bold cursor-pointer whitespace-nowrap inline-flex items-center gap-2 w-full sm:w-auto justify-center"
              style={{ minWidth: '200px' }}
            >
              <i className="ri-whatsapp-line" />
              Demo Talep Et
            </button>
            <button
              onClick={() => openModal('Final CTA — WhatsApp\'tan Yaz')}
              className="btn-outline-neon px-10 py-4 rounded-xl text-base cursor-pointer whitespace-nowrap inline-flex items-center gap-2 w-full sm:w-auto justify-center"
              style={{ minWidth: '200px' }}
            >
              <i className="ri-whatsapp-line" />
              WhatsApp&apos;tan Yaz
            </button>
          </div>

          {/* Trust signals */}
          <div
            className="cta-reveal flex flex-wrap items-center justify-center gap-6 text-gray-500 text-sm"
            style={{ opacity: 0, transform: 'translateY(20px)', transition: 'all 0.5s ease' }}
          >
            {[
              { icon: 'ri-shield-check-line', text: '100+ Memnun İşletme' },
              { icon: 'ri-timer-flash-line', text: '24 Saat Kurulum' },
              { icon: 'ri-customer-service-2-line', text: '7/24 Destek' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-1.5">
                <i className={`${item.icon} text-gold text-base`} />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 border-t border-white/5" style={{ background: '#070709' }}>
        <div className="max-w-6xl mx-auto px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src="https://static.readdy.ai/image/63ce3e67fd2d5effcfb662b2683f46ed/caaea5a1b17e64d200d3c08d14d4a01f.png"
              alt="Smart Hizmet Platform"
              className="h-8 w-auto object-contain opacity-80"
            />
          </div>
          <p className="text-gray-600 text-sm text-center">
            © 2024 Smart Hizmet Platform. Tüm hakları saklıdır.
          </p>
          <div className="flex items-center gap-4 text-gray-500 text-sm">
            <span>Size müşteri getiren sistem.</span>
          </div>
        </div>
      </footer>
    </>
  );
}
